import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './HomePage.module.css';
import { getDesignViewportSize } from '../../components/FixedScale/FixedScale';

const HomePage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const scrollContainer = containerRef.current;

    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.refresh();

    const scene = new THREE.Scene();
    scene.background = null;

    // No desktop o conteúdo vive dentro da caixa de design do FixedScale, então
    // o canvas precisa ter o tamanho dela (e não o da janela) para cobrir a
    // tela corretamente depois do scale.
    const initialViewport = getDesignViewportSize();

    const camera = new THREE.PerspectiveCamera(60, initialViewport.width / initialViewport.height, 0.1, 1000);
    camera.position.set(0, 0, 5);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setSize(initialViewport.width, initialViewport.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const canvasContainer = scrollContainer.querySelector(`.${styles.canvasContainer}`);
    canvasContainer?.appendChild(renderer.domElement);

    const cubeGroup = new THREE.Group();
    scene.add(cubeGroup);

    const geometry = new THREE.BoxGeometry(2, 2, 2, 4, 4, 4);

    const vertexShader = `
      varying vec2 vUv;
      varying vec3 vPosition;
      varying vec3 vNormal;
      void main() {
        vUv = uv;
        vPosition = position;
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const uniforms = {
      iTime: { value: 0 },
      iResolution: { value: new THREE.Vector2(512, 512) },
      scrollProgress: { value: 0.0 }
    };

    const fragmentShader = `
      uniform float iTime;
      uniform vec2 iResolution;
      uniform float scrollProgress;
      varying vec2 vUv;
      varying vec3 vPosition;
      varying vec3 vNormal;
      void mainImage(out vec4 O, vec2 I) {
          vec2 r = iResolution.xy;
          vec2 z;
          vec2 i;
          vec2 f = I*(z+=4.-4.*abs(.7-dot(I=(I+I-r)/r.y, I)));
          float timeOffset = sin(iTime * 0.2) * 0.1;
          f.x += timeOffset;
          f.y -= timeOffset;
          float iterations = mix(8.0, 12.0, scrollProgress);
          for(O *= 0.; i.y++<iterations;
              O += (sin(f += cos(f.yx*i.y+i+iTime)/i.y+.7)+1.).xyyx
              * abs(f.x-f.y));
          O = tanh(7.*exp(z.x-4.-I.y*vec4(-1,1,2,0))/O);
          float pulse = 1.0 + 0.2 * sin(iTime * 0.5);
          O.rgb *= pulse;
          float nebula = sin(I.x * 0.01 + iTime * 0.3) * sin(I.y * 0.01 - iTime * 0.2);
          nebula = abs(nebula) * 0.5;
          vec3 color1 = mix(vec3(0.1, 0.2, 0.8), vec3(0.8, 0.1, 0.5), scrollProgress);
          vec3 color2 = mix(vec3(0.8, 0.2, 0.7), vec3(0.2, 0.8, 0.7), scrollProgress);
          vec3 colorMix = mix(color1, color2, sin(iTime * 0.2) * 0.5 + 0.5);
          O.rgb = mix(O.rgb, colorMix, nebula * (1.0 - length(O.rgb)));
      }
      void main() {
          vec2 cubeUV = vUv * iResolution;
          vec4 fragColor;
          mainImage(fragColor, cubeUV);
          float depthFactor = abs(dot(vNormal, vec3(0.0, 0.0, 1.0)));
          fragColor.rgb *= 0.7 + 0.3 * depthFactor;
          float edge = 1.0 - max(abs(vUv.x - 0.5), abs(vUv.y - 0.5)) * 2.0;
          edge = pow(edge, 4.0);
          fragColor.rgb += edge * vec3(0.1, 0.2, 0.8) * (0.6 + scrollProgress * 0.4);
          fragColor.rgb *= 2.0;
          gl_FragColor = fragColor;
      }
    `;

    const material = new THREE.ShaderMaterial({
      vertexShader, fragmentShader, uniforms,
      transparent: true, opacity: 1.0, side: THREE.DoubleSide
    });

    const cube = new THREE.Mesh(geometry, material);
    cubeGroup.add(cube);

    const wireframe = new THREE.LineSegments(
      new THREE.EdgesGeometry(geometry, 10),
      new THREE.LineBasicMaterial({ color: 0x4488ff, linewidth: 1.5, transparent: true, opacity: 0.1 })
    );
    wireframe.scale.setScalar(1.001);
    cubeGroup.add(wireframe);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);

    ScrollTrigger.defaults({ scroller: scrollContainer });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: ".content",
        start: "top top",
        end: "bottom bottom",
        scrub: 1.5,
        onUpdate: self => {
          uniforms.scrollProgress.value = self.progress;
        }
      }
    });
    tl.to(cubeGroup.rotation, { x: Math.PI * 1.2, y: Math.PI * 2, z: Math.PI * 0.3, ease: "power2.inOut" });

    document.querySelectorAll(".section").forEach((section) => {
      gsap.fromTo(section.querySelector('.title'),
        { opacity: 0, y: 50 },
        {
          opacity: 1, y: 0, duration: 1, ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "top 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
      gsap.fromTo(section.querySelector('.description'),
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0, duration: 1, ease: "power2.out", delay: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 70%",
            end: "top 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    let animationFrameId: number;
    const clock = new THREE.Clock();
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      uniforms.iTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const { width, height } = getDesignViewportSize();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());

      scene.traverse(object => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach(m => m.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      renderer.dispose();
      if (canvasContainer && renderer.domElement) {
        canvasContainer.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <main ref={containerRef} className={styles.mainContainer}>
      <div className={styles.canvasContainer}></div>
      <div className={`${styles.content} content`}>
        <section className={`${styles.section} section`}>
          <div className={styles.sectionInner}>
            <h1 className={`${styles.title} title`}>CLYDES</h1>
            <p className={`${styles.description} description`}><h2>A DEMOCRACIA DO INACESSÍVEL</h2>Clydes começou com uma ideia simples:
              o acesso a recursos ainda é um privilégio.</p>
          </div>
        </section>
        <section className={`${styles.section} section`}>
          <div className={styles.sectionInner}>
            <h1 className={`${styles.title} title`}>fazer mais<br /> com menos</h1>
            <p className={`${styles.description} description`}><h2>Muita gente tem talento com poucas ferramentas.</h2>
              E as oportunidades seguem concentradas nos mesmos lugares de sempre.

              A gente decidiu fazer diferente.
              Trabalhar com quem produz, mesmo com pouco.
              Criar soluções reais — visuais, sonoras, acessíveis de verdade.</p>
          </div>
        </section>
        <section className={`${styles.section} section`}>
          <div className={styles.sectionInner}>
            <h1 className={`${styles.title} title`}>que fazemos</h1>
            <p className={`${styles.description} description`}><h2>Nos movemos entre música, arte, tecnologia e moda.</h2>
              Sem seguir tendências. Sem falar só com nichos. Desenvolvemos plugins, lançamos projetos, realizamos eventos e criamos roupas.
              Mas, mais do que produtos, o que buscamos é ampliar a democratização.</p>
          </div>
        </section>
        <section className={`${styles.section} section`}>
          <div className={styles.sectionInner}>
            <h1 className={`${styles.title} title`}>Criada pra quem?</h1>
            <p className={`${styles.description} description`}>
              <h2> </h2>
              Pra quem aprendeu a fazer com o que tinha.
              Pra quem sempre improvisou, adaptou, arriscou.
              Pra quem já se acostumou a criar nas bordas, longe do centro.
              Pra quem tá cansado de ver a arte virando vitrine — e não cultura.
            </p>
          </div>


        </section>

        <section className={`${styles.section} section`}>
          <div className={styles.sectionInner}>
            <h1 className={`${styles.title} title`}>É nois!</h1>
            <p className={`${styles.description} description`}>
              <h2> </h2>
            </p>
          </div>


        </section>

        {/* === ADICIONADO: O espaçador que garante a rolagem final === */}
        <div className={styles.spacer}></div>

      </div>
      <footer className={styles.footer}>
        <p>Clydes © 2024</p>
      </footer>
    </main>
  );
};

export default HomePage;